/**
 * Pie chart view component using Recharts.
 */

import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { ChartConfig, TransformedChartData } from '../../types/chartTypes';
import { DEFAULT_CHART_COLORS } from '../../types/chartTypes';
import { formatTooltipValue } from '../../utils/axisFormat';

type PieChartViewProps = {
  data: TransformedChartData;
  config: ChartConfig;
  height?: number | `${number}%`;
  /** Recharts default is true; dashboard tiles animate only on first mount. */
  isAnimationActive?: boolean;
  /** Raw mark-click passthrough (S11) — ChartRenderer normalizes/resolves. */
  onMark?: (...args: unknown[]) => void;
  /** ResponsiveContainer resize debounce (ms) — dashboard tiles pass ~150. */
  resizeDebounceMs?: number;
};

export function PieChartView({ data, config, height = 400, isAnimationActive, onMark, resizeDebounceMs }: PieChartViewProps) {
  const colors = config.colors || DEFAULT_CHART_COLORS;

  if (data.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No data to display
      </div>
    );
  }

  // For pie charts, we use the first (and should be only) series key
  const valueKey = data.seriesKeys[0] || 'value';

  return (
    <ResponsiveContainer width="100%" height={height} debounce={resizeDebounceMs}>
      {/* Tight margins: the slice labels already sit in the 30% the
          percentage `outerRadius` leaves free, so a wide margin on top of
          that only shrinks the pie inside a small tile. */}
      <PieChart margin={{ top: 8, right: 16, left: 16, bottom: 8 }}>
        <Pie
          isAnimationActive={isAnimationActive}
          data={data.data}
          dataKey={valueKey}
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={typeof height === 'number' ? Math.min(height / 3, 150) : '70%'}
          label={({ name, percent }) =>
            `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`
          }
          labelLine={{ stroke: 'var(--muted-foreground)' }}
          // Recharts separates slices with a hard-coded white stroke, which
          // reads as a bright halo in dark mode. Track the surface instead so
          // the separator works in both themes (--card === --background here,
          // and the HTML snapshot export defines the same var).
          stroke="var(--card)"
          onClick={onMark}
          cursor={onMark ? 'pointer' : undefined}
        >
          {data.data.map((_, index) => (
            <Cell
              key={`cell-${index}`}
              fill={colors[index % colors.length]}
            />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--background)',
            border: '1px solid var(--border)',
            borderRadius: '0.375rem',
          }}
          formatter={(value) => [formatTooltipValue(value), valueKey]}
        />
        {config.showLegend && <Legend verticalAlign="top" height={36} />}
      </PieChart>
    </ResponsiveContainer>
  );
}
